/**
 * <MessageBubble> — un message dans une conversation (molecule).
 *
 * Quatre faces, et c'est la FORME qui dit qui parle, jamais une couleur de champion :
 *  - `own` — le message de l'élève : l'aplat de son univers, encre blanche, calé à droite par la
 *    ligne qui le porte ;
 *  - `agent` — « l'agent parle en bulle » (livre, chap. 07) : une bulle sur une surface, sa queue
 *    tournée vers la tête de celui qui parle ;
 *  - `human` — « l'humain est cité avec sa photo » : un bloc aux coins de bloc, sans queue ni
 *    liseré. C'est sa ligne qui le cite : sa photo et son nom en tête de salve. Le liseré jaune
 *    des premières maquettes est retiré (Wajih, 13/10) ;
 *  - `unsent` — le message de l'élève qui n'est pas parti : il perd son aplat et passe sur la
 *    tinte d'erreur. Le rouge d'erreur et le corail de l'élève sont trop voisins pour qu'une
 *    simple couleur le dise ; c'est l'aplat retiré qui se lit.
 *
 * Le contenu : un texte, et/ou une pièce (`<MessageAttachment>`), ou un enfant libre (les points
 * de `<TypingIndicator>`). `format="rich"` passe le texte à `<RichText variant="message">`
 * (markdown + formules), en Outfit comme le reste du chat et sans les repères d'un énoncé : une
 * liste numérotée de Sam reste une liste (Wajih, 21/10). C'est un moteur lourd — une WebView par
 * bulle sur mobile — donc le défaut reste le texte simple, et seule une réponse qui porte une
 * formule ou une mise en forme le demande. Une bulle
 * `own` est toujours en texte simple : l'élève tape du texte, et `<RichText>` n'écrit pas en blanc.
 *
 * Avec `onPressLink`, les liens du texte (adresse web, téléphone, e-mail) se soulignent et se
 * touchent, comme dans l'ancien fil : la bulle les trouve (`messageSegments`), l'écran les ouvre.
 * Sans, le texte reste simple — Sam et les checkpoints ne l'ont jamais eu.
 *
 * La surface est celle de `<Card>` ; la bulle ne fait que régler ses coins et son retrait.
 */
import React, { useMemo } from "react"
import { Pressable } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { inkOnFill, reliefOnFill, tintOnFill, type SemanticColorKey } from "../../theme/tokens/colors"
import { type SpacingKey } from "../../theme/tokens/spacing"
import { type FontSizeKey } from "../../theme/tokens/typography"
import { frenchSpacing } from "../../utils/frenchSpacing"
import { Card } from "../Card"
import { MessageAttachment, type MessageVoiceControls, type UIMessageAttachment } from "../MessageAttachment"
import { RichText } from "../RichText"
import { messageSegments, type UIMessageLink } from "./messageLinks"

export type MessageBubbleFace = "own" | "agent" | "human" | "unsent"

export type MessageBubbleProps = {
    face: MessageBubbleFace
    text?: string
    /** `plain` par défaut. `rich` : markdown + formules, pour une face `agent` ou `human`. */
    format?: "plain" | "rich"
    attachment?: UIMessageAttachment
    /** La dernière bulle d'une salve porte la queue. Sans effet sur `human`, qui n'en a pas. */
    tail?: boolean
    /** Image ou document touché. */
    onOpenAttachment?: () => void
    /** Vocal : en lecture, son avancée (0 à 1), lire ou mettre en pause. */
    voicePlaying?: boolean
    voiceProgress?: number
    onToggleVoice?: () => void
    /** Vocal chargé : le curseur et la vitesse. Absent : la lecture seule. */
    voiceControls?: MessageVoiceControls
    /** Un lien du texte touché : l'écran l'ouvre. Absent, le texte reste simple. */
    onPressLink?: (link: UIMessageLink) => void
    /** Appui long sur la bulle (« Copier »). Absent, la bulle ne presse pas. */
    onLongPress?: () => void
    /** Un contenu libre à la place du texte — les points de « est en train d'écrire ». */
    children?: React.ReactNode
    testID?: string
}

type Face = {
    fill: SemanticColorKey
    ink: SemanticColorKey
    /** La piste de l'avancée d'un vocal, lisible sur l'aplat. */
    track: SemanticColorKey
    tail: "left" | "right" | null
}

/** L'aplat de celui qui écrit : celui du bouton de son univers. */
const OWN_FILL: SemanticColorKey = "role-fill"
const UNSENT = tintOnFill("danger")

const FACES: Record<MessageBubbleFace, Face> = {
    own: { fill: OWN_FILL, ink: inkOnFill(OWN_FILL), track: reliefOnFill(OWN_FILL), tail: "right" },
    agent: { fill: "surface-raised", ink: "text-primary", track: "border", tail: "left" },
    human: { fill: "surface-raised", ink: "text-primary", track: "border", tail: null },
    unsent: { fill: UNSENT.on, ink: UNSENT.ink, track: "border", tail: "right" },
}

/** Ce qu'un lecteur d'écran dit du geste que la bulle offre. */
const LONG_PRESS_HINT = "Appui long pour copier"

/** Au-delà, une ligne de texte devient trop longue pour se lire d'un trait. */
const MESSAGE_BUBBLE_MAX_WIDTH = 520

/** Le texte d'une bulle et son retrait vertical : ce qui fait la hauteur d'une bulle d'une ligne. */
export const MESSAGE_BUBBLE_TEXT_SIZE = "text-lg" satisfies FontSizeKey
export const MESSAGE_BUBBLE_PADDING_Y = "3" satisfies SpacingKey

export function MessageBubble(props: MessageBubbleProps) {
    const {
        face,
        text,
        format = "plain",
        attachment,
        tail = false,
        onOpenAttachment,
        voicePlaying,
        voiceProgress,
        onToggleVoice,
        voiceControls,
        onPressLink,
        onLongPress,
        children,
        testID,
    } = props

    const { fill, ink, track, tail: tailSide } = FACES[face]
    const hasText = text != null && text !== ""
    const rich = hasText && format === "rich" && (face === "agent" || face === "human")
    // Une image seule remplit sa bulle : le retrait n'est plus que son cadre.
    const framed = attachment?.kind === "image" && !hasText && children == null

    const bubble = (
        <Card
            background={fill}
            style={[
                styles.bubble(framed),
                face === "human" ? styles.quote : undefined,
                tail && tailSide === "right" ? styles.tailRight : undefined,
                tail && tailSide === "left" ? styles.tailLeft : undefined,
                rich ? styles.rich : undefined,
            ]}
            testID={onLongPress == null ? testID : undefined}
        >
            {children ?? (
                <Stack gap="2">
                    {attachment != null && (
                        <MessageAttachment
                            attachment={attachment}
                            ink={ink}
                            fill={fill}
                            track={track}
                            onOpen={onOpenAttachment}
                            playing={voicePlaying}
                            progress={voiceProgress}
                            onTogglePlay={onToggleVoice}
                            voiceControls={voiceControls}
                        />
                    )}
                    {hasText && rich && <RichText value={text} variant="message" />}
                    {hasText && !rich && (
                        <Text size={MESSAGE_BUBBLE_TEXT_SIZE} color={ink}>
                            {onPressLink != null ? (
                                <LinkedText text={text} ink={ink} onPressLink={onPressLink} />
                            ) : (
                                frenchSpacing(text)
                            )}
                        </Text>
                    )}
                </Stack>
            )}
        </Card>
    )

    if (onLongPress == null) return bubble

    return (
        <Pressable
            onLongPress={onLongPress}
            accessibilityHint={LONG_PRESS_HINT}
            style={rich ? styles.rich : styles.shrink}
            testID={testID}
        >
            {bubble}
        </Pressable>
    )
}

type LinkedTextProps = { text: string; ink: SemanticColorKey; onPressLink: (link: UIMessageLink) => void }

/** Le texte en morceaux : les liens soulignés, à l'encre de la bulle, et qui se touchent. */
function LinkedText(props: LinkedTextProps) {
    const { text, ink, onPressLink } = props
    const segments = useMemo(() => messageSegments(text), [text])
    return (
        <>
            {segments.map((segment, index) => {
                const link = segment.link
                if (link == null) return frenchSpacing(segment.text)
                return (
                    <Text
                        key={index}
                        size={MESSAGE_BUBBLE_TEXT_SIZE}
                        color={ink}
                        style={styles.link}
                        accessibilityRole="link"
                        onPress={() => onPressLink(link)}
                    >
                        {segment.text}
                    </Text>
                )
            })}
        </>
    )
}

const styles = StyleSheet.create((theme) => ({
    /** Un lien se lit souligné, à l'encre de la bulle : sur l'aplat de l'élève comme sur la surface. */
    link: { textDecorationLine: "underline" },
    bubble: (framed: boolean) => ({
        flexShrink: 1,
        maxWidth: MESSAGE_BUBBLE_MAX_WIDTH,
        paddingVertical: framed ? theme.spacing["1"] : theme.spacing[MESSAGE_BUBBLE_PADDING_Y],
        paddingHorizontal: framed ? theme.spacing["1"] : theme.spacing["4"],
    }),
    /** La citation : les coins d'un bloc, pas ceux d'une bulle. */
    quote: { borderRadius: theme.radius.md },
    /** La queue : le coin tourné vers celui qui parle se resserre. */
    tailRight: { borderBottomRightRadius: theme.radius.sm / 2 },
    tailLeft: { borderBottomLeftRadius: theme.radius.sm / 2 },
    /** Un texte riche a besoin d'une largeur : sa bulle prend celle que la ligne lui laisse. */
    rich: { flexShrink: 1, flexGrow: 1, maxWidth: MESSAGE_BUBBLE_MAX_WIDTH },
    shrink: { flexShrink: 1 },
}))
